import React, { useContext, useEffect, useRef } from "react";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { Button } from "@mui/material";

import {
  MeetingStatus,
  useLocalVideo,
  useMeetingManager,
} from "amazon-chime-sdk-component-library-react";

import { MeetingInfoContext } from "../../../context";
import { useUploadFile } from "../../../store";

interface UploadImageButtonProps {
  reportImageUploaded?: () => void;
  onUploadStart?: () => void;
  onUploadFailure?: (message: string) => void;
  label?: string;
  fullWidth?: boolean;
}

const UploadImageButton: React.FC<UploadImageButtonProps> = ({
  reportImageUploaded,
  onUploadStart,
  onUploadFailure,
  label = "Upload Photo",
  fullWidth = false,
}) => {
  const [uploading, setUploading] = React.useState(false);
  const [wasVideoEnabled, setWasVideoEnabled] = React.useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const meetingManager = useMeetingManager();
  const { isVideoEnabled, toggleVideo } = useLocalVideo();
  const { userId, meetingId, joinCode, joinType } =
    useContext(MeetingInfoContext);

  const canUpload =
    !uploading && meetingManager.meetingStatus === MeetingStatus.Succeeded;

  const waitToRestartVideo = () => {
    console.log(
      "~~~ waitToRestartVideo ",
      isVideoEnabled,
      "was",
      wasVideoEnabled,
    );
    // Only re-enable if we disabled video before opening the file picker.
    if (!wasVideoEnabled || isVideoEnabled) {
      return;
    }

    setWasVideoEnabled(false);

    setTimeout(() => {
      toggleVideo();
    }, 1000);
  };

  const onCancel = () => {
    console.log("~~~ Image uploader cancelled ~~~");
    waitToRestartVideo();
  };

  const onFileUploaded = () => {
    setUploading(false);
    waitToRestartVideo();

    if (reportImageUploaded) {
      reportImageUploaded();
    }
  };

  const onFileFailedUpload = (error: unknown) => {
    setUploading(false);
    onCancel();

    const errorMessage =
      error instanceof Error ? error.message : "Error uploading file";
    if (onUploadFailure) {
      onUploadFailure(errorMessage);
    }
  };

  const { mutate: createURL } = useUploadFile(
    onFileUploaded,
    onFileFailedUpload,
  );

  const handleImageUpload = (e: any) => {
    const [file] = e.target.files;
    if (!file) {
      console.log("No file uploaded");
      onCancel();
      return;
    }

    if (onUploadStart) {
      onUploadStart();
    }

    createURL({
      file,
      meetingId,
      userId,
      joinCode,
      joinType,
    });

    setUploading(true);
  };

  const onUploaderClick = () => {
    fileInputRef.current?.click();
    setWasVideoEnabled(isVideoEnabled);

    if (isVideoEnabled) {
      toggleVideo();
    }
  };

  useEffect(() => {
    if (!fileInputRef.current) {
      return;
    }

    fileInputRef.current.addEventListener("cancel", onCancel);

    // remove event listener when component unmounts
    return () => {
      console.log("~~~ Image uploader unmounted ~~~");
      if (!fileInputRef.current) {
        return;
      }

      fileInputRef.current.removeEventListener("cancel", onCancel);
    };
  }, [fileInputRef]);

  return (
    <>
      <Button
        variant="contained"
        fullWidth={fullWidth}
        disabled={!canUpload}
        startIcon={<FileUploadIcon />}
        onClick={onUploaderClick}
        sx={{
          borderRadius: "1.5rem",
          px: 2,
          py: 1,
          height: "40px",
          fontWeight: 600,
          textTransform: "none",
        }}
      >
        {label}
      </Button>
      <input
        title={"Upload Photo Input"}
        name={"Upload Photo Input"}
        type="file"
        accept={"image/*"}
        capture={"environment"}
        onChange={handleImageUpload}
        ref={fileInputRef}
        aria-hidden={true}
        style={{
          display: "none",
        }}
      />
    </>
  );
};

export default UploadImageButton;
